import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, User, CreditCard, LogOut, Loader2, AlertTriangle, Eye, EyeOff, Lock, Users, Wrench, Clock, ArrowRight, Shield, CheckCircle2 } from 'lucide-react';
import LightBackground from '@/components/LightBackground';
import LightLogo from '@/components/LightLogo';
import { SupportChat } from '@/components/SupportChat';
import Footer from '@/components/Footer';
import { academiesData } from '@/data/academyData';
import { useAuth } from '@/hooks/useAuth';
import { useModuleProgress } from '@/hooks/useModuleProgress';
import { useSubscription } from '@/hooks/useSubscription';
import { useClientAccess } from '@/hooks/useClientAccess';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TranslatedText } from '@/components/TranslatedText';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const { academyProgress, completedModules, isLoading: progressLoading, getAcademyProgress } = useModuleProgress();
  const { createCheckoutSession, openCustomerPortal, loading: subscriptionLoading } = useSubscription();
  const { loading: clientAccessLoading, clientInfo, showSubscriptionWall: shouldShowSubscriptionWall, showUpgradeBanner } = useClientAccess();
  const [memberName, setMemberName] = useState<string>('');
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [bypassSubscriptionWall, setBypassSubscriptionWall] = useState(false);
  
  // Password change modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  // Show subscription wall unless user chose to proceed as freemium
  const showSubscriptionWall = shouldShowSubscriptionWall && !bypassSubscriptionWall;
  
  // Check if user needs to set password (from email link)
  useEffect(() => {
    const requirePassword = searchParams.get('require_password');
    if (requirePassword === 'true' && user) {
      setShowPasswordModal(true);
      // Clear the URL param
      searchParams.delete('require_password');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, user, setSearchParams]);
  
  const handlePasswordUpdate = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      
      toast.success('Password created successfully!');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Remove custom cursor on dashboard (light theme uses normal cursor)
  useEffect(() => {
    document.body.style.cursor = 'auto';
  }, []);

  // Require authentication
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Admins should never land on the members dashboard
  useEffect(() => {
    if (!authLoading && user && role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, role, navigate]);

  // Fetch member name from client record or sessionStorage fallback
  useEffect(() => {
    const fetchMemberName = async () => {
      if (!user) return;
      
      // First try to get from client record
      const { data } = await supabase
        .from('clients')
        .select('name')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data) {
        const firstName = data.name?.split(' ')[0];
        setMemberName(firstName || '');
        return;
      }
      
      // Fallback: check sessionStorage for freemium users who just signed up
      const freemiumIntent = sessionStorage.getItem('freemiumIntent');
      if (freemiumIntent) {
        try {
          const { name } = JSON.parse(freemiumIntent);
          const firstName = name?.split(' ')[0];
          setMemberName(firstName || '');
        } catch (e) {
          // Ignore parse errors
        }
      }
    };
    
    fetchMemberName();
  }, [user]);

  // Derive values from clientInfo
  const isLifetimeMember = clientInfo.isLifetimeAccess;
  const daysRemaining = clientInfo.daysRemaining;
  const isTestAccount = clientInfo.isTestAccount;

  // Fetch academy statuses from database
  const [academyStatuses, setAcademyStatuses] = useState<Record<string, string>>({});
  
  useEffect(() => {
    const fetchAcademyStatuses = async () => {
      const { data } = await supabase
        .from('academies')
        .select('id, status');
      
      if (data) {
        const statusMap: Record<string, string> = {};
        data.forEach((academy) => {
          statusMap[academy.id] = academy.status;
        });
        setAcademyStatuses(statusMap);
      }
    };
    
    fetchAcademyStatuses();
  }, []);

  // Calculate real progress for each academy
  const academies = useMemo(() => {
    return academiesData.map((academy, index) => {
      const progress = getAcademyProgress(academy.id);
      const progressPercent = progress.total > 0 
        ? Math.round((progress.completed / progress.total) * 100) 
        : 0;
      
      return {
        id: academy.id,
        name: academy.name,
        description: academy.description,
        modules: academy.modules.length,
        progress: progressPercent,
        completedModules: progress.completed,
        totalModules: progress.total,
        unlocked: true, // Full access - 5K plan
        status: academyStatuses[academy.id] || 'active',
      };
    });
  }, [academyProgress, getAcademyProgress, academyStatuses]);

  // Track class completions for stats
  const [classCompletions, setClassCompletions] = useState<number>(0);
  
  // Total classes is fixed at 500 (5 academies × 10 modules × 10 classes)
  const TOTAL_CLASSES = 500;

  // Fetch class completions count
  useEffect(() => {
    const fetchClassStats = async () => {
      if (!user) return;
      
      // Get client ID first
      const { data: clientData } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!clientData) return;
      
      // Get count of passed class completions
      const { count: completedCount } = await supabase
        .from('class_completions')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', clientData.id)
        .eq('passed', true);
      
      setClassCompletions(completedCount || 0);
    };
    
    fetchClassStats();
  }, [user]);

  // Calculate overall stats
  const stats = useMemo(() => {
    const academiesCompleted = academies.filter(a => a.progress === 100).length;
    const totalAcademies = academies.length;
    const classPercentage = Math.round((classCompletions / TOTAL_CLASSES) * 100);
    
    return {
      academiesCompleted: `${academiesCompleted}/${totalAcademies}`,
      classesCompleted: classCompletions.toString(),
      classPercentage: `${classPercentage}%`,
    };
  }, [academies, classCompletions]);

  // Show loading while checking auth, subscription, or progress
  if (authLoading || clientAccessLoading || progressLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Subscription Wall
  if (showSubscriptionWall) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <LightBackground variant="default" />
        
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-100 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 md:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center w-full max-w-5xl"
          >
            <div className="mb-8 flex justify-center">
              <LightLogo size="lg" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              <TranslatedText>Complete Your Membership</TranslatedText>
            </h1>

            <p className="text-slate-600 mb-8 max-w-lg mx-auto">
              <TranslatedText>Welcome back! To access the FA Club dashboard and all member benefits, please choose your subscription plan.</TranslatedText>
            </p>

            {/* Plan Options */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8 w-full">
              {/* Monthly Plan */}
              <motion.div
                className="relative p-6 sm:p-8 lg:p-10 bg-white rounded-2xl shadow-lg border border-slate-200 flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="relative z-10 flex flex-col h-full">
                  {/* Plan title */}
                  <div className="text-center mb-6">
                    <p className="text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase text-slate-500 mb-1 sm:mb-2">
                      Full Access
                    </p>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Monthly</h3>
                  </div>

                  {/* Price display */}
                  <div className="text-center mb-6">
                    <div className="mb-1">
                      <span className="text-base sm:text-lg text-slate-400 line-through">$49</span>
                    </div>
                    <div className="flex items-baseline justify-center gap-1 sm:gap-2">
                      <span className="text-4xl sm:text-5xl font-bold text-slate-900">
                        $4.99
                      </span>
                      <span className="text-sm sm:text-lg text-slate-500 font-light">/month</span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-slate-500 mt-2 px-2">Pay as you learn • Unlock new classes gradually</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6" />

                  {/* Features list */}
                  <div className="space-y-2 sm:space-y-3 mb-6 flex-1">
                    {[
                      "Modules unlock as you complete each one",
                      "Automated templates and systems",
                      "Secret knowledge library",
                      "Networking with the faceless elite",
                      "Monthly access to content",
                      "Priority 24/7 support",
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-2 sm:gap-3">
                        <div className="w-3 sm:w-4 h-3 sm:h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        </div>
                        <span className="text-xs sm:text-sm text-slate-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    size="lg"
                    className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold h-14 rounded-xl"
                    disabled={isProcessingCheckout}
                    onClick={async () => {
                      try {
                        setIsProcessingCheckout(true);
                        await createCheckoutSession('monthly');
                      } catch (error) {
                        console.error('Checkout error:', error);
                        toast.error('Failed to start checkout. Please try again.');
                      } finally {
                        setIsProcessingCheckout(false);
                      }
                    }}
                  >
                    {isProcessingCheckout ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Become a Member'
                    )}
                  </Button>
                </div>
              </motion.div>

              {/* Lifetime Plan - Featured */}
              <motion.div
                className="relative p-6 sm:p-8 lg:p-10 bg-white rounded-2xl shadow-xl border-2 border-blue-200 flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Best Value Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="px-4 py-1 bg-blue-600 text-white text-xs font-bold uppercase rounded-full shadow-lg">
                    Best Value
                  </span>
                </div>

                <div className="relative z-10 flex flex-col h-full">
                  {/* Plan title */}
                  <div className="text-center mb-6">
                    <p className="text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase text-blue-600/60 mb-1 sm:mb-2">
                      One-Time Payment
                    </p>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Lifetime Access</h3>
                  </div>

                  {/* Price display */}
                  <div className="text-center mb-6">
                    <div className="mb-1">
                      <span className="text-base sm:text-lg text-slate-400 line-through">$197</span>
                    </div>
                    <div className="flex items-baseline justify-center gap-1 sm:gap-2">
                      <span className="text-4xl sm:text-5xl font-bold text-blue-600">
                        $37
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-blue-600/60 mt-2 px-2">🚀 Access Everything from day one • No monthly fees ever</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-blue-200 to-transparent mb-6" />

                  {/* Features list */}
                  <div className="space-y-3 mb-6 flex-1">
                    {[
                      "All 5 academies unlocked instantly",
                      "All 50 modules available on spot",
                      "All 500 classes accessible immediately",
                      "All 500 exercises ready to use",
                      "Automated templates and systems",
                      "Networking with the faceless elite",
                      "Priority 24/7 support",
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                        </div>
                        <span className="text-sm text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 rounded-xl shadow-lg shadow-blue-600/25"
                    disabled={isProcessingCheckout}
                    onClick={async () => {
                      try {
                        setIsProcessingCheckout(true);
                        await createCheckoutSession('lifetime');
                      } catch (error) {
                        console.error('Checkout error:', error);
                        toast.error('Failed to start checkout. Please try again.');
                      } finally {
                        setIsProcessingCheckout(false);
                      }
                    }}
                  >
                    {isProcessingCheckout ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Get Lifetime Access'
                    )}
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Trust elements */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-6">
              <Shield className="w-4 h-4 text-green-500" />
              <span>Secure payment • 7-day guarantee</span>
            </div>

            {/* Freemium Option */}
            <div
              onClick={async () => {
                if (user) {
                  try {
                    const { data: existingClient } = await supabase
                      .from('clients')
                      .select('id')
                      .eq('user_id', user.id)
                      .maybeSingle();
                    
                    if (!existingClient) {
                      const { error } = await supabase.rpc('create_freemium_client' as any, {
                        p_user_id: user.id,
                        p_email: user.email || '',
                        p_name: user.user_metadata?.name || user.email?.split('@')[0] || 'Member'
                      });
                      
                      if (error) {
                        console.error('Error creating freemium client:', error);
                        toast.error('Failed to set up freemium access');
                        return;
                      }
                    }
                  } catch (err) {
                    console.error('Freemium setup error:', err);
                  }
                }
                setBypassSubscriptionWall(true);
                toast.success('Welcome! You have access to Academy 1, Module 1, Class 1.');
              }}
              className="cursor-pointer rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 mb-6 hover:border-blue-400 hover:shadow-lg transition-all duration-300 active:scale-[0.98]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-blue-700 mb-1">🎓 Watch 1 FREE Class</p>
                  <p className="text-sm text-slate-500">No credit card required • Instant access</p>
                </div>
                <div className="text-blue-500">
                  <ArrowRight className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-blue-600 font-medium">
                <CheckCircle2 className="w-4 h-4" />
                <span>Preview Academy 1, Module 1, Class 1 for free</span>
              </div>
            </div>

            <p className="text-slate-500 text-sm mb-4">
              Need help? Contact us at{' '}
              <a href="mailto:info@facelessaura.club" className="text-blue-600 hover:underline">
                info@facelessaura.club
              </a>
            </p>

            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              className="text-slate-500 hover:text-blue-600"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />
      
      {/* Password Change Modal - keeping existing logic but updating styles */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
            >
              <div className="text-center mb-6">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900"><TranslatedText>Create Your Password</TranslatedText></h2>
                <p className="text-sm text-slate-600 mt-2">
                  <TranslatedText>Welcome to FA Club! Please create a secure password for your account.</TranslatedText>
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="New password (min 6 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 pr-10 h-12 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 pr-10 h-12 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                <Button
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 rounded-xl"
                  onClick={handlePasswordUpdate}
                  disabled={isUpdatingPassword || !newPassword || !confirmPassword}
                >
                  {isUpdatingPassword ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Password'
                  )}
                </Button>
                
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="w-full text-sm text-slate-500 hover:text-blue-600 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Freemium Account Upgrade Banner (freemium only) */}
      {clientInfo.clientType === 'freemium' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-30 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-200 px-6 py-3"
        >
          <div className="flex items-center justify-center gap-3 text-sm flex-wrap">
            <span className="text-slate-700">
              You are using a FREE TEST account.
            </span>
            <span className="text-slate-500">
              Upgrade to access ALL 500 CLASSES
            </span>
            <button
              onClick={() => navigate('/profile', { state: { openSubscription: true } })}
              className="ml-2 px-4 py-1.5 text-xs font-semibold uppercase bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
            >
              Check Prices
            </button>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-slate-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/" className="flex items-center">
          <LightLogo size="md" />
        </Link>
        <div className="flex items-center gap-2 sm:gap-4">
          <LanguageSelector variant="ghost" className="text-slate-600 hover:text-slate-900" />
          {isTestAccount && (
            <span className="px-3 py-1 text-xs font-medium uppercase bg-purple-100 border border-purple-200 text-purple-700 rounded-full">
              🧪 Test {clientInfo.clientType === 'test_lifetime' ? 'Lifetime' : 'Monthly'}
            </span>
          )}
          {!isTestAccount && isLifetimeMember ? (
            <span className="px-3 py-1 text-xs font-medium uppercase bg-blue-100 border border-blue-200 text-blue-700 rounded-full">
              ✦ Lifetime
            </span>
          ) : !isTestAccount && clientInfo.clientType === 'monthly' && daysRemaining !== null && (
            <span className="px-3 py-1 text-xs bg-slate-100 border border-slate-200 text-slate-600 rounded-full">
              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
            </span>
          )}
          <span className="text-sm text-slate-600"><TranslatedText>Welcome</TranslatedText>, {memberName || 'Member'}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-slate-600 hover:text-blue-600 hover:bg-blue-50">
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white border-slate-200">
              <DropdownMenuItem 
                className="text-slate-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
                onClick={() => navigate('/profile')}
              >
                <User className="mr-2 h-4 w-4" />
                <TranslatedText>Profile</TranslatedText>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-slate-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
                onClick={() => navigate('/affiliates')}
              >
                <Users className="mr-2 h-4 w-4" />
                <TranslatedText>Affiliates Program</TranslatedText>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-slate-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
                onClick={async () => {
                  if (clientInfo.clientType === 'freemium') {
                    setBypassSubscriptionWall(false);
                    return;
                  }
                  try {
                    await openCustomerPortal();
                  } catch (error: any) {
                    console.error('Portal error:', error);
                    if (error?.message?.includes('No Stripe customer found')) {
                      toast.error('Subscription management is not available for your account type. Please contact support.');
                    } else {
                      toast.error('Failed to open billing portal. Please try again.');
                    }
                  }
                }}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                <TranslatedText>Manage Subscription</TranslatedText>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-200" />
              <DropdownMenuItem 
                className="text-slate-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
                onClick={async () => { await signOut(); }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <TranslatedText>Sign Out</TranslatedText>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-12">
        {/* Welcome section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            <TranslatedText>Members Dashboard</TranslatedText>
          </h1>
          <p className="text-slate-600">
            <TranslatedText>Your journey to faceless mastery begins here</TranslatedText>
          </p>
        </motion.div>

        {/* Stats row */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {[
            { label: 'Academies Completed', value: stats.academiesCompleted },
            { label: 'Modules Completed', value: completedModules.length.toString() },
            { label: 'Classes Completed', value: stats.classesCompleted },
            { label: '% of All Classes Completed', value: stats.classPercentage },
          ].map((stat, index) => (
            <div key={index} className="p-6 bg-white rounded-2xl shadow-md border border-slate-100 text-center">
              <p className="text-2xl md:text-3xl font-bold text-blue-600 mb-1">{stat.value}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider"><TranslatedText>{stat.label}</TranslatedText></p>
            </div>
          ))}
        </motion.div>

        {/* Academies grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-xl font-bold text-slate-900 mb-6"><TranslatedText>Your Academies</TranslatedText></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {academies.map((academy, index) => (
              <motion.div
                key={index}
                className={`relative p-6 rounded-2xl bg-white shadow-md border ${
                  academy.unlocked ? 'border-blue-200 hover:border-blue-400' : 'border-slate-200 opacity-60'
                } transition-all`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              >
                {academy.status === 'maintenance' && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-amber-100 border border-amber-200 rounded text-amber-700">
                    <Wrench className="w-3 h-3" />
                    <span className="text-[10px] uppercase font-medium"><TranslatedText>Maintenance</TranslatedText></span>
                  </div>
                )}
                {academy.status === 'coming_soon' && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-blue-100 border border-blue-200 rounded text-blue-700">
                    <Clock className="w-3 h-3" />
                    <span className="text-[10px] uppercase font-medium"><TranslatedText>Coming Soon</TranslatedText></span>
                  </div>
                )}

                <span className="text-[10px] text-slate-400 uppercase tracking-widest"><TranslatedText>Academy</TranslatedText> {index + 1}</span>
                <h3 className={`text-lg font-semibold mb-2 ${academy.unlocked ? 'text-slate-900' : 'text-slate-500'}`}>
                  <TranslatedText cacheKey={`academy_name_${academy.id}`}>{academy.name}</TranslatedText>
                </h3>
                <p className="text-xs text-slate-500 mb-4">{academy.modules} <TranslatedText>modules</TranslatedText></p>

                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden mb-4">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${academy.progress}%` }}
                    transition={{ duration: 1, delay: 0.8 + index * 0.1 }}
                  />
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">{academy.progress}% <TranslatedText>complete</TranslatedText></span>
                  {academy.unlocked ? (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-blue-600 hover:bg-blue-50 text-xs"
                      onClick={() => navigate(`/academy/${academy.id}`)}
                    >
                      <TranslatedText>Continue</TranslatedText> <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400"><TranslatedText>Locked</TranslatedText></span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quick actions */}
        <motion.div
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="p-6 border border-slate-200 bg-white rounded-2xl text-center hover:border-blue-300 hover:shadow-md transition-all cursor-pointer">
            <p className="text-blue-600 font-semibold text-sm mb-1"><TranslatedText>Private Community</TranslatedText></p>
            <p className="text-xs text-slate-500"><TranslatedText>Network with elite members</TranslatedText></p>
          </div>
          <div className="p-6 border border-slate-200 bg-white rounded-2xl text-center hover:border-blue-300 hover:shadow-md transition-all cursor-pointer">
            <p className="text-blue-600 font-semibold text-sm mb-1"><TranslatedText>24/7 Support</TranslatedText></p>
            <p className="text-xs text-slate-500"><TranslatedText>We're here to help you succeed</TranslatedText></p>
          </div>
          <a 
            href="mailto:support@facelessaura.club" 
            className="p-6 border border-slate-200 bg-white rounded-2xl text-center hover:border-blue-300 hover:shadow-md transition-all block"
          >
            <p className="text-blue-600 font-semibold text-sm mb-1"><TranslatedText>Email Support</TranslatedText></p>
            <p className="text-xs text-slate-500">support@facelessaura.club</p>
          </a>
        </motion.div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      <SupportChat />
    </div>
  );
};

export default Dashboard;